import { useCallback, useEffect, useRef, useState } from "react";
import { attachAnalyser, detachAnalyser, audioBus } from "../audioBus";
import { DEFAULT_FX, type AudioFX, type FxUnit } from "../audioFx";

export type AudioSourceKind = "none" | "system" | "file";

const FFT = 2048;

/**
 * Feeds the swarm's audio reactivity. Two ways in:
 *
 *  - `connectSystem()` — captures the audio the user already has playing on
 *    their computer (Spotify, a browser tab, a media player). The ONLY browser
 *    path to desktop audio (short of the microphone) is the screen-capture API
 *    with its "Share system audio" option, so this pops Chrome's screen picker;
 *    we keep the audio track and ignore the video. Chrome/Edge on Windows.
 *  - `connectFile(file)` — plays a dropped-in audio file and visualises it
 *    (also audible, since we DO route it to the speakers).
 *
 * Either way we build one AnalyserNode and hand it to `audioBus`; the live
 * levels never touch React (only the coarse source/label/error/gain do).
 */
export function useAudio() {
  const [source, setSource] = useState<AudioSourceKind>("none");
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");
  const [gain, setGain] = useState(1);
  const [fx, setFx] = useState<AudioFX>(DEFAULT_FX);

  /** Patch one effect unit, e.g. `setFxUnit("push")({ frac: 0.3 })`. */
  const setFxUnit = useCallback(
    (key: keyof AudioFX) => (patch: Partial<FxUnit & { frac: number }>) =>
      setFx((s) => ({ ...s, [key]: { ...s[key], ...patch } })),
    []
  );

  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const elRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioBus.gain = gain;
  }, [gain]);

  const teardown = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (elRef.current) {
      elRef.current.pause();
      const url = elRef.current.src;
      elRef.current.src = "";
      if (url.startsWith("blob:")) URL.revokeObjectURL(url);
      elRef.current = null;
    }
    detachAnalyser();
    ctxRef.current?.close().catch(() => {});
    ctxRef.current = null;
  }, []);

  const disconnect = useCallback(() => {
    teardown();
    setSource("none");
    setLabel("");
  }, [teardown]);

  const makeAnalyser = (ctx: AudioContext) => {
    const analyser = ctx.createAnalyser();
    analyser.fftSize = FFT;
    analyser.smoothingTimeConstant = 0.6;
    return analyser;
  };

  const connectSystem = useCallback(async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 5 },
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        systemAudio: "include",
      } as DisplayMediaStreamOptions & { systemAudio?: string });

      const aTracks = stream.getAudioTracks();
      if (aTracks.length === 0) {
        stream.getTracks().forEach((t) => t.stop());
        setError('No audio captured — pick "Entire Screen" and tick "Share system audio".');
        return;
      }

      teardown();
      streamRef.current = stream;
      const ctx = new AudioContext();
      await ctx.resume();
      ctxRef.current = ctx;
      const analyser = makeAnalyser(ctx);
      // Keep only the audio; the screen video track is unused (low frame rate).
      ctx.createMediaStreamSource(new MediaStream(aTracks)).connect(analyser);
      // NOT routed to destination — the system audio is already audible.
      attachAnalyser(analyser, ctx.sampleRate);
      setSource("system");
      setLabel("System audio");
      // Chrome's "Stop sharing" ends the track — mirror that in our state.
      aTracks[0].addEventListener("ended", disconnect);
    } catch (e) {
      const err = e as DOMException;
      if (err?.name !== "NotAllowedError") setError(err?.message || "Couldn't start audio capture.");
    }
  }, [teardown, disconnect]);

  const connectFile = useCallback(
    async (file: File) => {
      setError("");
      try {
        teardown();
        const ctx = new AudioContext();
        await ctx.resume();
        ctxRef.current = ctx;
        const el = new Audio();
        el.src = URL.createObjectURL(file);
        el.loop = true;
        elRef.current = el;
        const analyser = makeAnalyser(ctx);
        const srcNode = ctx.createMediaElementSource(el);
        srcNode.connect(analyser);
        srcNode.connect(ctx.destination); // a loaded file we DO want to hear
        attachAnalyser(analyser, ctx.sampleRate);
        await el.play();
        setSource("file");
        setLabel(file.name);
      } catch (e) {
        const err = e as Error;
        setError(err?.message || "Couldn't play that file.");
      }
    },
    [teardown]
  );

  useEffect(() => () => teardown(), [teardown]);

  return {
    source,
    label,
    error,
    gain,
    setGain,
    fx,
    setFxUnit,
    active: source !== "none",
    connectSystem,
    connectFile,
    disconnect,
  };
}
